import { useState, type ComponentPropsWithoutRef, type ComponentType } from "react";
import { useLocation } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import {
  Building2,
  ChevronDown,
  CircleQuestionMark,
  FileText,
  Gauge,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Menu,
  MessageSquareMore,
  PanelTop,
  PackageSearch,
  ScanSearch,
  Settings,
  Shield,
  Users,
  Video,
  Wrench,
} from "lucide-react";

import { api } from "@constructionautomation/backend";
import { useTheme, type Theme } from "@/lib/theme";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { cn } from "@/lib/utils";
import { AppLink } from "@/components/app-link";
import { ProjectSwitcher } from "@/components/projects";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useWorkspace } from "@/features/workspaces";
import { useUserProfile } from "@/components/auth";
import { FeedbackDialog } from "@/components/feedback/feedback-dialog";

interface TopNavProps {
  className?: string;
}
const themeOptions: Array<{ id: Theme; label: string }> = [
  { id: "office", label: "Office" },
  { id: "blueprint", label: "Blueprint" },
  { id: "construction", label: "Construction" },
  { id: "field", label: "Field" },
  { id: "duck-hunt", label: "Duck Hunt" },
];

function MenuButton({
  label,
  icon: Icon,
  active = false,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Button> & {
  label: string;
  icon: ComponentType<{ className?: string }>;
  active?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      className={cn(
        "h-9 gap-2 px-3 text-sm font-semibold text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        active &&
          "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground",
        className
      )}
      {...props}
    >
      <Icon className="size-4 shrink-0" />
      <span>{label}</span>
      <ChevronDown className="size-4 opacity-70" />
    </Button>
  );
}

function NavMenuItem({
  onSelect,
  icon: Icon,
  children,
}: {
  onSelect: () => void;
  icon?: ComponentType<{ className?: string }>;
  children: string;
}) {
  return (
    <DropdownMenuItem onSelect={onSelect}>
      {Icon ? <Icon className="size-4" /> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </DropdownMenuItem>
  );
}

export function TopNav({ className }: TopNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { user, signOut } = useAuth();
  const { userProfile, isPlatformAdmin } = useUserProfile();
  const { data: unreadCount } = useQuery(convexQuery(api.notifications.getUnreadCount, {}));
  const { isEnabled, toggleWorkspaces } = useWorkspace();
  const { theme, setTheme } = useTheme();
  const navigate = useAppNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const canManageOrganization =
    userProfile?.orgRole === "owner" || userProfile?.orgRole === "admin";
  const organizationLogoUrl = userProfile?.organizationLogoUrl ?? "/favicon-32x32.png";
  const organizationName = userProfile?.organizationName ?? "Construction Cortex";

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
  };

  const goTo = (to: string, title?: string, params?: Record<string, string>) => {
    navigate({ to, title, params });
    closeAllMenus();
  };

  const isSpecPilotRoute =
    pathname === "/specPilot" || pathname === "/specPilot/new" || pathname.startsWith("/specPilot/");
  const isRevPilotRoute = pathname === "/revPilot" || pathname.startsWith("/revPilot/");
  const isTakeoffPilotRoute =
    pathname === "/takeoffPilot" ||
    pathname === "/takeoffPilot/new" ||
    pathname.startsWith("/takeoffPilot/");
  const isProcurePilotRoute =
    pathname === "/procurePilot" ||
    pathname === "/procurePilot/new" ||
    pathname.startsWith("/procurePilot/");
  const isRevCollabRoute = pathname === "/revCollab" || pathname.startsWith("/revCollab/");
  const isProjectDocumentsRoute = pathname === "/project/documents" || pathname.startsWith("/project/documents/");
  const isAdminRoute = pathname === "/admin" || pathname.startsWith("/admin/");
  const isInboxRoute = pathname === "/inbox";

  const handleSignOut = async () => {
    await signOut({ navigate: false });
    window.location.reload();
  };

  const renderProjectsSection = () => {
    return (
      <div className="flex items-center gap-2">
        <ProjectSwitcher />
      </div>
    );
  };

  const renderToolsMenu = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <MenuButton
            label="Tools"
            icon={Wrench}
            active={isSpecPilotRoute || isRevPilotRoute || isTakeoffPilotRoute || isProcurePilotRoute || isRevCollabRoute || isProjectDocumentsRoute}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 border-2" align="start">
          <DropdownMenuLabel>Tools</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <NavMenuItem icon={FileText} onSelect={() => goTo("/project/documents", "Documents")}>
              Documents
          </NavMenuItem>
          <NavMenuItem icon={LayoutDashboard} onSelect={() => goTo("/specPilot", "SpecPilot")}>
              SpecPilot
          </NavMenuItem>
          <NavMenuItem icon={ScanSearch} onSelect={() => goTo("/takeoffPilot", "TakeoffPilot")}>
              TakeoffPilot
          </NavMenuItem>
          <NavMenuItem icon={PackageSearch} onSelect={() => goTo("/procurePilot", "ProcurePilot")}>
              ProcurePilot
          </NavMenuItem>
          <NavMenuItem icon={PanelTop} onSelect={() => goTo("/revPilot", "RevPilot")}>
              RevPilot
          </NavMenuItem>
          <NavMenuItem icon={MessageSquareMore} onSelect={() => goTo("/revCollab", "CollabPilot")}>
              CollabPilot
          </NavMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderAdminMenu = () => {
    if (!isPlatformAdmin) {
      return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <MenuButton
            label="Admin"
            icon={Shield}
            active={isAdminRoute}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 border-2" align="start">
          <DropdownMenuLabel>Admin</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <NavMenuItem icon={Gauge} onSelect={() => goTo("/admin", "Cost Dashboard")}>
            Cost Dashboard
          </NavMenuItem>
          <NavMenuItem icon={Building2} onSelect={() => goTo("/admin/billing", "Billing") }>
            Billing
          </NavMenuItem>
          <NavMenuItem icon={Users} onSelect={() => goTo("/admin/roles", "Roles") }>
            Roles
          </NavMenuItem>
          <NavMenuItem icon={MessageSquareMore} onSelect={() => goTo("/admin/feedback", "Feedback") }>
            Feedback
          </NavMenuItem>
          <NavMenuItem icon={Video} onSelect={() => goTo("/admin/training", "Training Videos") }>
            Training Videos
          </NavMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderSupportMenu = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="Support and feedback"
          >
            <CircleQuestionMark className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 border-2" align="end">
          <DropdownMenuLabel>Support</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setFeedbackOpen(true)}>
            <MessageSquareMore className="size-4" />
            <span>Feedback</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <CircleQuestionMark className="size-4" />
            <span>Support</span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => goTo("/training", "Training Videos")}>
            <Video className="size-4" />
            <span>Training Videos</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <header
      data-slot="top-nav"
      className={cn("border-b-2 border-sidebar-border bg-sidebar text-sidebar-foreground", className)}
    >
      <div className="flex h-16 items-center gap-2 px-4 lg:px-6">
        <AppLink to="/" className="mr-1 flex items-center gap-2.5">
          <img
            src={organizationLogoUrl}
            alt={`${organizationName} Logo`}
            className="size-8 rounded object-contain"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/favicon-32x32.png";
            }}
          />
          <span className="hidden text-base font-bold tracking-tight sm:inline">
            Construction Cortex
          </span>
        </AppLink>

        <div className="ml-8 hidden items-center gap-2 md:flex">
          {renderProjectsSection()}
          {renderToolsMenu()}
          {renderAdminMenu()}
        </div>

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            className={cn(
              "relative text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              isInboxRoute &&
                "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground"
            )}
            onClick={() => goTo("/inbox", "Inbox")}
            aria-label="Inbox"
          >
            <Mail className="size-4" />
            {(unreadCount ?? 0) > 0 ? (
              <Badge
                variant="secondary"
                className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 text-[10px]"
              >
                {Math.min(unreadCount ?? 0, 99)}
              </Badge>
            ) : null}
          </Button>

          {renderSupportMenu()}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                aria-label="Settings"
              >
                <Settings className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-60 border-2" align="end">
              <DropdownMenuLabel>Settings</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={theme}
                onValueChange={(value) => setTheme(value as Theme)}
              >
                {themeOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.id} value={option.id}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={isEnabled}
                onCheckedChange={(checked) => toggleWorkspaces(Boolean(checked))}
              >
                Enable Workspaces
              </DropdownMenuCheckboxItem>
              {canManageOrganization ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => goTo("/organization", "Organization")}>
                    <Building2 className="size-4" />
                    <span>Organization</span>
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>

          <span className="hidden max-w-56 truncate px-2 text-xs font-semibold text-sidebar-foreground/80 md:inline">
            {user?.email}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="h-9 gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label="Sign out"
          >
            <LogOut className="size-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>

          <Button
            variant="ghost"
            size="icon-sm"
            className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:hidden"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            <Menu className="size-5" />
          </Button>
        </div>
      </div>

      {mobileMenuOpen ? (
        <div className="border-t-2 border-sidebar-border p-3 md:hidden">
          <div className="grid gap-2">
            <div className="flex flex-col gap-2">
              <ProjectSwitcher />
              <Button
                variant="ghost"
                className="justify-start gap-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                onClick={() => goTo("/projects", "Projects")}
              >
                <ListChecks className="size-4" />
                All Projects
              </Button>
            </div>
            {renderToolsMenu()}
            {renderAdminMenu()}
          </div>
        </div>
      ) : null}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </header>
  );
}

/*
 * Usage Example:
 *
 * import { TopNav } from "@/components/layout";
 *
 * <TopNav />
 */
